import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../DataLayouts/Header";
import toast from "react-hot-toast";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { MAIN_URL } from "../../../Configurations/Urls";
import axios from "axios";
import { fetchOrganizationDepartment } from "../../../Apis/Department-api";
import { fetchOrganizationEmploymentTypes } from "../../../Apis/Organization-Employement-types";
import { fetchEntity } from "../../../Apis/Entity";
import { fetchOrganizationEmployee } from "../../../Apis/Employee-api";
import { fetchWorkModel } from "../../../Apis/Workshift-api";

const OPENING_TYPES = ["Employment", "Internship", "Apprenticeship", "Trainee"];
const GENDER_PREFERENCES = ["Any", "Male", "Female", "Other"];
const PRIORITIES = ["Low", "Medium", "High", "Urgent"];
const STATUSES = ["Draft", "Open", "On Hold", "Closed", "Cancelled"];

const asList = (payload, keys = []) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  for (const key of keys) {
    const val = payload?.[key];
    if (Array.isArray(val)) return val;
    if (Array.isArray(val?.data)) return val.data;
  }
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const locationLabel = (loc) =>
  loc?.location?.location_name ||
  loc?.locations?.location_name ||
  loc?.organization_location?.location_name ||
  loc?.location_name ||
  (loc?.organization_department_location_id
    ? `#${loc.organization_department_location_id}`
    : "");

const emptyToNull = (value) => (value === "" || value === undefined ? null : value);
const sliceDate = (value) => (value ? String(value).slice(0, 10) : "");

const employeeLabel = (emp) => {
  if (!emp) return "";
  const name =
    [emp.first_name, emp.last_name].filter(Boolean).join(" ") ||
    emp.name ||
    emp.full_name ||
    "";
  return emp.employee_code ? `${name} (${emp.employee_code})` : name;
};

const INITIAL_FORM = {
  opening_title: "",
  opening_type: "Employment",
  organization_entity_id: "",
  organization_unit_id: "",
  organization_department_id: "",
  organization_department_location_id: "",
  organization_designation_id: "",
  organization_employment_type_id: "",
  organization_work_model_id: "",
  number_of_openings: 1,
  minimum_experience_years: "",
  maximum_experience_years: "",
  minimum_age: "",
  maximum_age: "",
  gender_preference: "Any",
  minimum_salary: "",
  maximum_salary: "",
  minimum_stipend: "",
  maximum_stipend: "",
  currency_code: "INR",
  internship_duration_months: "",
  probation_period_days: "",
  opening_date: "",
  application_deadline: "",
  expected_joining_date: "",
  priority: "Medium",
  status: "Open",
  hiring_manager_employee_id: "",
  recruiter_employee_id: "",
  description: "",
};

function RecruitmentOpeningsForm({ mode }) {
  const { id } = useParams();
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const navigate = useNavigate();
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(mode === "edit" || mode === "view");
  const [btnLoading, setBtnLoading] = useState(false);
  const [entities, setEntities] = useState([]);
  const [units, setUnits] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [deptLocations, setDeptLocations] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [workModels, setWorkModels] = useState([]);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    if (!org?.organization_id) return;
    const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

    Promise.allSettled([
      fetchEntity(org.organization_id),
      axios.get(`${MAIN_URL}/api/organizations/${org.organization_id}/units?per_page=all`, { headers }),
      fetchOrganizationDepartment(org.organization_id),
      fetchOrganizationEmploymentTypes(org.organization_id),
      fetchWorkModel(org.organization_id),
      fetchOrganizationEmployee(org.organization_id),
      axios.get(`${MAIN_URL}/api/organizations/${org.organization_id}/work-model?per_page=all`, { headers }),
    ]).then((results) => {
      const [entityRes, unitRes, deptRes, typeRes, modelRes, empRes, modelAxios] = results;
      if (entityRes.status === "fulfilled") {
        setEntities(asList(entityRes.value, ["entity", "entities", "data"]));
      }
      if (unitRes.status === "fulfilled") {
        setUnits(asList(unitRes.value?.data, ["Unit", "units", "Units", "data"]));
      }
      if (deptRes.status === "fulfilled") {
        setDepartments(asList(deptRes.value, ["Departments", "departments", "data"]));
      }
      if (typeRes.status === "fulfilled") {
        setEmploymentTypes(
          asList(typeRes.value, ["employemtType", "employmenttypes", "employment_types", "data"])
        );
      }
      const models =
        modelRes.status === "fulfilled"
          ? asList(modelRes.value, ["workmodel", "workmodels", "work_models", "data"])
          : [];
      if (models.length) {
        setWorkModels(models);
      } else if (modelAxios.status === "fulfilled") {
        setWorkModels(asList(modelAxios.value?.data, ["workmodel", "workmodels", "work_models", "data"]));
      }
      if (empRes.status === "fulfilled") {
        setEmployees(asList(empRes.value, ["employees", "Employees", "data"]));
      }
    });
  }, [org?.organization_id]);

  useEffect(() => {
    const departmentId = formData.organization_department_id;
    if (!departmentId || !org?.organization_id) {
      setDeptLocations([]);
      setDesignations([]);
      return;
    }

    const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    const params = {
      department_id: departmentId,
      organization_id: org.organization_id,
    };

    axios
      .get(`${MAIN_URL}/api/department-locations/all`, { headers, params })
      .then((res) => setDeptLocations(asList(res.data, ["data"])))
      .catch(() => setDeptLocations([]));

    axios
      .get(`${MAIN_URL}/api/department-designation/all`, { headers, params })
      .then((res) => setDesignations(asList(res.data, ["data"])))
      .catch(() => setDesignations([]));
  }, [formData.organization_department_id, org?.organization_id]);

  useEffect(() => {
    const loadById = async () => {
      try {
        const response = await axios.get(
          `${MAIN_URL}/api/organizations/${org.organization_id}/recruitment-opening/${id}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
        );
        const a = response?.data?.recruitment_opening || {};
        setFormData({
          ...INITIAL_FORM,
          opening_title: a.opening_title || "",
          opening_type: a.opening_type || "Employment",
          organization_entity_id: a.organization_entity_id || "",
          organization_unit_id: a.organization_unit_id || "",
          organization_department_id: a.organization_department_id || "",
          organization_department_location_id: a.organization_department_location_id || "",
          organization_designation_id: a.organization_designation_id || "",
          organization_employment_type_id: a.organization_employment_type_id || "",
          organization_work_model_id: a.organization_work_model_id || "",
          number_of_openings: a.number_of_openings ?? 1,
          minimum_experience_years: a.minimum_experience_years ?? "",
          maximum_experience_years: a.maximum_experience_years ?? "",
          minimum_age: a.minimum_age ?? "",
          maximum_age: a.maximum_age ?? "",
          gender_preference: a.gender_preference || "Any",
          minimum_salary: a.minimum_salary ?? "",
          maximum_salary: a.maximum_salary ?? "",
          minimum_stipend: a.minimum_stipend ?? "",
          maximum_stipend: a.maximum_stipend ?? "",
          currency_code: a.currency_code || "INR",
          internship_duration_months: a.internship_duration_months ?? "",
          probation_period_days: a.probation_period_days ?? "",
          opening_date: sliceDate(a.opening_date),
          application_deadline: sliceDate(a.application_deadline),
          expected_joining_date: sliceDate(a.expected_joining_date),
          priority: a.priority || "Medium",
          status: a.status || "Open",
          hiring_manager_employee_id: a.hiring_manager_employee_id || "",
          recruiter_employee_id: a.recruiter_employee_id || "",
          description: a.description || "",
        });
      } catch (err) {
        console.error(err);
        toast.error("Failed to load recruitment opening");
      } finally {
        setLoading(false);
      }
    };

    if ((mode === "edit" || mode === "view") && id && org?.organization_id) {
      setLoading(true);
      loadById();
    }
  }, [mode, id, org?.organization_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "organization_department_id") {
      setFormData((prev) => ({
        ...prev,
        organization_department_id: value,
        organization_department_location_id: "",
        organization_designation_id: "",
      }));
      setFormErrors((prev) => ({
        ...prev,
        organization_department_id: "",
        organization_department_location_id: "",
        organization_designation_id: "",
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.opening_title?.trim()) errors.opening_title = "Opening title is required.";
    if (!formData.opening_type) errors.opening_type = "Opening type is required.";
    if (!formData.opening_date) errors.opening_date = "Opening date is required.";
    if (
      formData.application_deadline &&
      formData.opening_date &&
      formData.application_deadline < formData.opening_date
    ) {
      errors.application_deadline = "Deadline cannot be before opening date.";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);
    const payload = {
      opening_title: formData.opening_title,
      opening_type: formData.opening_type,
      organization_entity_id: emptyToNull(formData.organization_entity_id),
      organization_unit_id: emptyToNull(formData.organization_unit_id),
      organization_department_id: emptyToNull(formData.organization_department_id),
      organization_department_location_id: emptyToNull(formData.organization_department_location_id),
      organization_designation_id: emptyToNull(formData.organization_designation_id),
      organization_employment_type_id: emptyToNull(formData.organization_employment_type_id),
      organization_work_model_id: emptyToNull(formData.organization_work_model_id),
      number_of_openings: Number(formData.number_of_openings) || 1,
      minimum_experience_years: emptyToNull(formData.minimum_experience_years),
      maximum_experience_years: emptyToNull(formData.maximum_experience_years),
      minimum_age: emptyToNull(formData.minimum_age),
      maximum_age: emptyToNull(formData.maximum_age),
      gender_preference: formData.gender_preference || "Any",
      minimum_salary: emptyToNull(formData.minimum_salary),
      maximum_salary: emptyToNull(formData.maximum_salary),
      minimum_stipend: emptyToNull(formData.minimum_stipend),
      maximum_stipend: emptyToNull(formData.maximum_stipend),
      currency_code: formData.currency_code || "INR",
      internship_duration_months: emptyToNull(formData.internship_duration_months),
      probation_period_days: emptyToNull(formData.probation_period_days),
      opening_date: formData.opening_date,
      application_deadline: emptyToNull(formData.application_deadline),
      expected_joining_date: emptyToNull(formData.expected_joining_date),
      priority: formData.priority || "Medium",
      status: formData.status || "Open",
      hiring_manager_employee_id: emptyToNull(formData.hiring_manager_employee_id),
      recruiter_employee_id: emptyToNull(formData.recruiter_employee_id),
      description: emptyToNull(formData.description),
    };

    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      if (mode === "edit") {
        await axios.put(
          `${MAIN_URL}/api/organizations/${org?.organization_id}/recruitment-opening/${id}`,
          payload,
          { headers }
        );
      } else {
        await axios.post(
          `${MAIN_URL}/api/organizations/${org?.organization_id}/recruitment-opening`,
          payload,
          { headers }
        );
      }
      toast.success(mode === "edit" ? "Recruitment Opening Updated!" : "Recruitment Opening Created!");
      navigate(-1);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 422) {
        const validationErrors = err.response.data.errors || {};
        setFormErrors(validationErrors);
        const errorMessages = Object.values(validationErrors)
          .map((arr) => (Array.isArray(arr) ? arr[0] : arr))
          .join(" ");
        toast.error(errorMessages || "Validation failed.");
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setBtnLoading(false);
    }
  };

  const selectField = (name, label, options, getValue, getLabel, required = false) => (
    <TextField
      select
      fullWidth
      label={label}
      name={name}
      disabled={mode === "view"}
      value={formData[name] || ""}
      onChange={handleChange}
      error={!!formErrors[name]}
      helperText={Array.isArray(formErrors[name]) ? formErrors[name][0] : formErrors[name]}
      required={required}
    >
      <MenuItem value="">None</MenuItem>
      {options.map((opt) => (
        <MenuItem key={getValue(opt)} value={getValue(opt)}>
          {getLabel(opt)}
        </MenuItem>
      ))}
    </TextField>
  );

  const mappingField = (name, label, options, getId, getLabel, extra = {}) => (
    <Autocomplete
      fullWidth
      options={options || []}
      getOptionLabel={(opt) => getLabel(opt) || ""}
      isOptionEqualToValue={(a, b) => String(getId(a)) === String(getId(b))}
      value={(options || []).find((o) => String(getId(o)) === String(formData[name])) || null}
      onChange={(_, newValue) => {
        const nextId = newValue ? getId(newValue) : "";
        if (name === "organization_department_id") {
          handleChange({ target: { name, value: nextId } });
          return;
        }
        setField(name, nextId);
      }}
      disabled={mode === "view" || extra.disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={!!formErrors[name]}
          helperText={Array.isArray(formErrors[name]) ? formErrors[name][0] : formErrors[name]}
        />
      )}
    />
  );

  return (
    <Header
      mode={mode}
      updateMessage={"Recruitment Opening"}
      addMessage={"Recruitment Opening"}
      homeLink={"/recruitment/openings"}
      homeText={"Recruitment Openings"}
      onSubmit={handleSubmit}
      loading={loading}
      btnLoading={btnLoading}
    >
      {loading ? (
        <Grid container spacing={2}>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </Grid>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={10}>
            <Paper elevation={4} sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={700}>Opening Details</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Opening Title"
                    name="opening_title"
                    disabled={mode === "view"}
                    value={formData.opening_title}
                    onChange={handleChange}
                    error={!!formErrors.opening_title}
                    helperText={formErrors.opening_title}
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  {selectField("opening_type", "Opening Type", OPENING_TYPES, (v) => v, (v) => v, true)}
                </Grid>
                <Grid item xs={12} md={3}>
                  {selectField("status", "Status", STATUSES, (v) => v, (v) => v)}
                </Grid>
                <Grid item xs={12} md={3}>
                  {selectField("priority", "Priority", PRIORITIES, (v) => v, (v) => v)}
                </Grid>
                <Grid item xs={12} md={3}>
                  {selectField("gender_preference", "Gender Preference", GENDER_PREFERENCES, (v) => v, (v) => v)}
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Number of Openings"
                    name="number_of_openings"
                    disabled={mode === "view"}
                    value={formData.number_of_openings}
                    onChange={handleChange}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Currency"
                    name="currency_code"
                    disabled={mode === "view"}
                    value={formData.currency_code}
                    onChange={handleChange}
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1 }}>Organization Mapping</Typography>
                </Grid>
                <Grid item xs={12}>
                  {mappingField("organization_entity_id", "Entity", entities, (o) => o.organization_entity_id, (o) => o.entity_name)}
                </Grid>
                <Grid item xs={12}>
                  {mappingField("organization_unit_id", "Unit", units, (o) => o.organization_unit_id, (o) => o.unit_name)}
                </Grid>
                <Grid item xs={12}>
                  {mappingField("organization_department_id", "Department", departments, (o) => o.organization_department_id, (o) => o.department_name)}
                </Grid>
                <Grid item xs={12}>
                  {mappingField(
                    "organization_department_location_id",
                    "Department Location",
                    deptLocations,
                    (o) => o.organization_department_location_id,
                    locationLabel,
                    { disabled: !formData.organization_department_id }
                  )}
                </Grid>
                <Grid item xs={12}>
                  {mappingField(
                    "organization_designation_id",
                    "Designation",
                    designations,
                    (o) => o.organization_designation_id,
                    (o) => o.designation_name,
                    { disabled: !formData.organization_department_id }
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {mappingField("organization_employment_type_id", "Employment Type", employmentTypes, (o) => o.organization_employment_type_id, (o) => o.employment_type_name)}
                </Grid>
                <Grid item xs={12} md={6}>
                  {mappingField("organization_work_model_id", "Work Model", workModels, (o) => o.organization_work_model_id, (o) => o.work_model_name)}
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1 }}>Dates & Compensation</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth required type="date" label="Opening Date" name="opening_date" disabled={mode === "view"} value={formData.opening_date} onChange={handleChange} error={!!formErrors.opening_date} helperText={formErrors.opening_date} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth type="date" label="Application Deadline" name="application_deadline" disabled={mode === "view"} value={formData.application_deadline} onChange={handleChange} error={!!formErrors.application_deadline} helperText={formErrors.application_deadline} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth type="date" label="Expected Joining Date" name="expected_joining_date" disabled={mode === "view"} value={formData.expected_joining_date} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth type="number" label="Min Experience (Years)" name="minimum_experience_years" disabled={mode === "view"} value={formData.minimum_experience_years} onChange={handleChange} inputProps={{ min: 0, step: 0.1 }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth type="number" label="Max Experience (Years)" name="maximum_experience_years" disabled={mode === "view"} value={formData.maximum_experience_years} onChange={handleChange} inputProps={{ min: 0, step: 0.1 }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth type="number" label="Min Age" name="minimum_age" disabled={mode === "view"} value={formData.minimum_age} onChange={handleChange} inputProps={{ min: 0 }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth type="number" label="Max Age" name="maximum_age" disabled={mode === "view"} value={formData.maximum_age} onChange={handleChange} inputProps={{ min: 0 }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth type="number" label="Min Salary" name="minimum_salary" disabled={mode === "view"} value={formData.minimum_salary} onChange={handleChange} inputProps={{ min: 0 }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth type="number" label="Max Salary" name="maximum_salary" disabled={mode === "view"} value={formData.maximum_salary} onChange={handleChange} inputProps={{ min: 0 }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth type="number" label="Min Stipend" name="minimum_stipend" disabled={mode === "view"} value={formData.minimum_stipend} onChange={handleChange} inputProps={{ min: 0 }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth type="number" label="Max Stipend" name="maximum_stipend" disabled={mode === "view"} value={formData.maximum_stipend} onChange={handleChange} inputProps={{ min: 0 }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth type="number" label="Internship Duration (Months)" name="internship_duration_months" disabled={mode === "view"} value={formData.internship_duration_months} onChange={handleChange} inputProps={{ min: 0 }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth type="number" label="Probation Period (Days)" name="probation_period_days" disabled={mode === "view"} value={formData.probation_period_days} onChange={handleChange} inputProps={{ min: 0 }} />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1 }}>Hiring Team</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={employees}
                    getOptionLabel={employeeLabel}
                    value={employees.find((e) => String(e.employee_id) === String(formData.hiring_manager_employee_id)) || null}
                    onChange={(_, newValue) => setField("hiring_manager_employee_id", newValue?.employee_id || "")}
                    disabled={mode === "view"}
                    renderInput={(params) => <TextField {...params} label="Hiring Manager" />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={employees}
                    getOptionLabel={employeeLabel}
                    value={employees.find((e) => String(e.employee_id) === String(formData.recruiter_employee_id)) || null}
                    onChange={(_, newValue) => setField("recruiter_employee_id", newValue?.employee_id || "")}
                    disabled={mode === "view"}
                    renderInput={(params) => <TextField {...params} label="Recruiter" />}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Description"
                    name="description"
                    disabled={mode === "view"}
                    value={formData.description}
                    onChange={handleChange}
                    inputProps={{ maxLength: 255 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="medium"
                    onClick={handleSubmit}
                    disabled={loading || btnLoading || mode === "view"}
                    sx={{ borderRadius: 2, minWidth: 120, textTransform: "capitalize", fontWeight: 500, mt: 1 }}
                  >
                    {btnLoading ? <CircularProgress size={24} color="inherit" /> : "Submit"}
                  </Button>
                  {mode === "edit" && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      onClick={() => navigate(-1)}
                      sx={{ borderRadius: 2, minWidth: 120, textTransform: "capitalize", fontWeight: 500, ml: 2, mt: 1 }}
                    >
                      Cancel
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Header>
  );
}

export default RecruitmentOpeningsForm;
